import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { useBoardTraversalModeV3 } from "../useBoardTraversalModeV3";
import type { LvMatch } from "../../utils/types";
import type { UseBoardSelectionV3Args } from "./types";

export const useBoardSelectionV3 = ({
  canEdit,
  diagram,
  isQuantityGroupApproved,
  toggleQuantityGroupApproval,
  onSetBoardItemLvMatch,
  onDeleteItemGroup,
  traversing,
}: UseBoardSelectionV3Args) => {
  const { t } = useTranslation();
  const diagramId = diagram?.diagram.id;
  const prevDiagramIdRef = useRef(diagramId);

  const [selectedItemTypeId, setSelectedItemTypeId] = useState<number | null>(
    () => diagram?.itemTypes[0]?.diagram_item_type_id ?? null,
  );
  const [selectedOccurrenceIndex, setSelectedOccurrenceIndex] = useState(0);

  const { isTraversing, startTraversing, stopTraversing } = traversing;

  const selectedItemType = useMemo(
    () =>
      diagram?.itemTypes.find(
        (itemType) => itemType.diagram_item_type_id === selectedItemTypeId,
      ) ?? null,
    [diagram, selectedItemTypeId],
  );

  const selectedOccurrences = selectedItemType?.occurrences ?? [];

  const { seenIds, markSeenAndCheckCompletion } = useBoardTraversalModeV3({
    canEdit,
    currentItemTypeId: selectedItemTypeId ?? 0,
    selectedOccurrences,
    isQuantityGroupApproved,
    toggleQuantityGroupApproval,
  });

  const selectedOccurrence = useMemo(
    () => selectedItemType?.occurrences[selectedOccurrenceIndex] ?? null,
    [selectedItemType, selectedOccurrenceIndex],
  );

  const occurrenceCount = selectedItemType?.occurrences.length ?? 0;
  const canGoPrev = selectedOccurrenceIndex > 0;
  const canGoNext = selectedOccurrenceIndex < occurrenceCount - 1;

  const markCurrentOccurrenceSeen = useCallback(
    (index: number) => {
      if (!isTraversing || !selectedItemType) return;
      const occurrence = selectedItemType.occurrences[index];
      if (occurrence) {
        markSeenAndCheckCompletion(String(occurrence.occurrence_id));
      }
    },
    [isTraversing, selectedItemType, markSeenAndCheckCompletion],
  );

  const handleSelectItemType = useCallback(
    (id: number) => {
      if (isTraversing) {
        stopTraversing();
      }
      setSelectedItemTypeId(id);
      setSelectedOccurrenceIndex(0);
    },
    [isTraversing, stopTraversing],
  );

  const handleSelectOccurrence = useCallback(
    (index: number) => {
      setSelectedOccurrenceIndex(index);
      if (isTraversing && selectedItemType) {
        const occurrence = selectedItemType.occurrences[index];
        if (occurrence) {
          markSeenAndCheckCompletion(String(occurrence.occurrence_id));
        }
      }
    },
    [isTraversing, selectedItemType, markSeenAndCheckCompletion],
  );

  const goNextOccurrence = useCallback(() => {
    setSelectedOccurrenceIndex((prev) => {
      if (prev >= occurrenceCount - 1) return prev;
      const nextIndex = prev + 1;
      markCurrentOccurrenceSeen(nextIndex);
      return nextIndex;
    });
  }, [occurrenceCount, markCurrentOccurrenceSeen]);

  const goPrevOccurrence = useCallback(() => {
    setSelectedOccurrenceIndex((prev) => {
      if (prev <= 0) return prev;
      const nextIndex = prev - 1;
      markCurrentOccurrenceSeen(nextIndex);
      return nextIndex;
    });
  }, [markCurrentOccurrenceSeen]);

  const goNextUnseen = useCallback(() => {
    if (!selectedItemType) return;
    const occurrences = selectedItemType.occurrences;
    for (let offset = 1; offset < occurrences.length; offset++) {
      const index = (selectedOccurrenceIndex + offset) % occurrences.length;
      if (!seenIds.has(String(occurrences[index].occurrence_id))) {
        handleSelectOccurrence(index);
        return;
      }
    }
  }, [
    selectedItemType,
    selectedOccurrenceIndex,
    seenIds,
    handleSelectOccurrence,
  ]);

  const selectFirstItemType = useCallback(() => {
    setSelectedItemTypeId(diagram?.itemTypes[0]?.diagram_item_type_id ?? null);
    setSelectedOccurrenceIndex(0);
  }, [diagram]);

  const handleSaveLvMatch = useCallback(
    async (lvMatch: LvMatch) => {
      if (!diagram || !selectedItemType) return;
      try {
        await api.updateQuantitySurveyorDiagramItemType(
          selectedItemType.diagram_item_type_id,
          {
            lvMatchId: lvMatch.lvId ?? null,
            lvMatchName: lvMatch.lvName ?? null,
            lvMatchConfidence: lvMatch.confidence ?? null,
            lvMatchReasoning: lvMatch.reasoning ?? null,
          },
        );
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_LV_MATCH_SAVED, {
          groupKind: "quantities",
          groupTypeId: String(selectedItemType.diagram_item_type_id),
        });
        onSetBoardItemLvMatch({
          diagramId: diagram.diagram.id,
          diagramItemTypeId: selectedItemType.diagram_item_type_id,
          lvMatch,
        });
      } catch {
        toast.error(t("smartSurveyor.lvMatchEditor.saveFailed"));
        throw new Error("Failed to save BOQ position");
      }
    },
    [diagram, selectedItemType, onSetBoardItemLvMatch, t],
  );

  const handleDeleteItemGroup = useCallback(async () => {
    if (!diagram || !selectedItemType) return;

    const deletedItemTypeId = selectedItemType.diagram_item_type_id;

    try {
      await api.deleteQuantitySurveyorDiagramItemType(deletedItemTypeId);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_DELETED, {
        groupKind: "quantities",
        groupTypeId: String(deletedItemTypeId),
        elementCount: selectedItemType.occurrences.length,
      });
      onDeleteItemGroup({
        diagramId: diagram.diagram.id,
        diagramItemTypeId: deletedItemTypeId,
      });

      const nextItemType =
        diagram.itemTypes.find(
          (itemType) => itemType.diagram_item_type_id !== deletedItemTypeId,
        ) ?? null;
      if (nextItemType) {
        handleSelectItemType(nextItemType.diagram_item_type_id);
      } else {
        if (isTraversing) {
          stopTraversing();
        }
        setSelectedItemTypeId(null);
        setSelectedOccurrenceIndex(0);
      }
    } catch {
      toast.error(t("smartSurveyor.groupInspector.deleteGroupFailed"));
    }
  }, [
    diagram,
    selectedItemType,
    isTraversing,
    stopTraversing,
    handleSelectItemType,
    onDeleteItemGroup,
    t,
  ]);

  const handleStartTraversing = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_TRAVERSAL_STARTED, {
      groupKind: "quantities",
    });
    startTraversing();
    setSelectedOccurrenceIndex(0);
    if (selectedItemType && selectedItemType.occurrences.length > 0) {
      const firstOccurrence = selectedItemType.occurrences[0];
      markSeenAndCheckCompletion(String(firstOccurrence.occurrence_id));
    }
  }, [startTraversing, selectedItemType, markSeenAndCheckCompletion]);

  useEffect(() => {
    if (diagramId === prevDiagramIdRef.current) return;
    prevDiagramIdRef.current = diagramId;
    setSelectedItemTypeId(diagram?.itemTypes[0]?.diagram_item_type_id ?? null);
    setSelectedOccurrenceIndex(0);
  }, [diagramId, diagram]);

  return {
    selectedItemTypeId,
    selectedItemType,
    selectedOccurrenceIndex,
    selectedOccurrence,
    occurrenceCount,
    canGoPrev,
    canGoNext,
    handleSelectItemType,
    handleSelectOccurrence,
    goNextOccurrence,
    goPrevOccurrence,
    goNextUnseen,
    selectFirstItemType,
    handleStartTraversing,
    seenIds,
    handleSaveLvMatch,
    handleDeleteItemGroup,
  } as const;
};
